import { cometh4337Params, getCometh4337Wallet } from '$services/cometh/getComethWallet';
import { sendUserOperation } from '$services/cometh/sendUserOperation';
import { type MetaTransaction } from '$types/transaction';
import { log } from '$utils/__log';
import { publicClient } from '$utils/publicClient';
import {
  type BatchRun,
  type SdkContractRunner,
  type TransactionRequest,
  type TransactionResponse,
} from '@circles-sdk/adapter';
import {
  type ComethSafeSmartAccount,
  type ComethSmartAccountClient,
} from '@cometh/connect-sdk-4337';
import { http, type Address, type Chain, type Hex, type Transport } from 'viem';
import { createBundlerClient } from 'viem/account-abstraction';
import { gnosis } from 'viem/chains';

type ContextComethSmartAccountClient = ComethSmartAccountClient<Transport>;

export class Cometh4337SdkContractRunner implements SdkContractRunner {
  private smartAccount!: ComethSafeSmartAccount;
  private smartAccountClient!: ContextComethSmartAccountClient;
  address?: Address;

  constructor(
    private readonly apiKey: string,
    private readonly chain: Chain,
    walletAddress?: Address,
  ) {
    this.address = walletAddress;
  }

  init = async () => {
    try {
      const { smartAccount, smartAccountClient } = await getCometh4337Wallet(
        this.address as Address,
      );

      this.smartAccount = smartAccount;
      this.smartAccountClient = smartAccountClient as unknown as ContextComethSmartAccountClient;

      this.address = this.smartAccount.address;
      return this.address;
    } catch (error) {
      log(error, 'error in cometh4337ContractRunner init');
    }
  };

  estimateGas = async (tx: TransactionRequest): Promise<bigint> => {
    const estimation = await publicClient.estimateGas({
      account: this.smartAccount.address,
      to: tx.to as Hex,
      data: tx.data as Hex,
      value: tx.value,
    });
    return BigInt(estimation.toString());
  };

  call = async (tx: TransactionRequest): Promise<string> => {
    const callResult = await publicClient.call({
      account: this.smartAccount.address,
      to: tx.to as Hex,
      data: tx.data as Hex,
      value: tx.value,
    });

    return callResult.data as string;
  };

  sendTransaction = async (tx: TransactionRequest): Promise<TransactionResponse> => {
    log(`Sending transaction to ${tx.to}`, tx);

    const userOpHash = await sendUserOperation({
      calls: [
        {
          to: tx.to as Hex,
          data: tx.data as Hex,
          value: tx.value,
        },
      ],
      address: this.address as Address,
      notOptimized: true,
    });

    const bundlerClient = createBundlerClient({
      chain: this.chain,
      transport: http(cometh4337Params.bundlerUrl),
    });

    const { receipt } = await bundlerClient.waitForUserOperationReceipt({
      hash: userOpHash,
    });

    return {
      blockNumber: Number(receipt.blockNumber),
      blockHash: receipt.blockHash,
      index: receipt.transactionIndex,
      hash: receipt.transactionHash,
      type: Number(receipt.type),
      to: tx.to,
      from: receipt.from,
      gasLimit: BigInt(receipt.gasUsed.toString()),
      gasPrice: BigInt(receipt.effectiveGasPrice.toString()),
      data: tx.data || '',
      value: tx.value,
      chainId: this.chain.id,
    };
  };

  sendBatchTransaction() {
    return new ComethBatchRun(this.smartAccountClient);
  }
}

export class ComethBatchRun implements BatchRun {
  private readonly transactions: TransactionRequest[] = [];

  constructor(private readonly smartAccountClient: ContextComethSmartAccountClient) {}

  addTransaction(tx: TransactionRequest) {
    this.transactions.push(tx);
  }

  async getTxCalldata(): Promise<MetaTransaction[]> {
    const metaTransactions: MetaTransaction[] = this.transactions.map((tx) => ({
      operation: 0,
      to: tx.to as Hex,
      value: BigInt(tx.value.toString()),
      data: tx.data as Hex,
    }));

    const callsForAggregate3Value = metaTransactions.map((tx) => ({
      to: tx.to,
      data: tx.data,
      value: tx.value ?? 0n,
    }));

    log(callsForAggregate3Value, 'callsForAggregate3Value');
    return callsForAggregate3Value;
  }

  async run(): Promise<TransactionResponse> {
    const userOpHash = await sendUserOperation({
      calls: this.transactions.map((tx) => ({
        to: tx.to as Hex,
        data: tx.data as Hex,
        value: tx.value,
      })),
      notOptimized: true,
      address: this.smartAccountClient.account?.address as Address,
    });

    const bundlerClient = createBundlerClient({
      chain: gnosis,
      transport: http(cometh4337Params.bundlerUrl),
    });

    const { receipt } = await bundlerClient.waitForUserOperationReceipt({
      hash: userOpHash,
    });

    return {
      blockNumber: Number(receipt.blockNumber),
      blockHash: receipt.blockHash,
      index: receipt.transactionIndex,
      hash: receipt.transactionHash,
      type: Number(receipt.type),
      to: receipt.to as Hex,
      from: receipt.from,
      gasLimit: BigInt(receipt.gasUsed.toString()),
      gasPrice: BigInt(receipt.effectiveGasPrice.toString()),
      data: '',
      value: BigInt(0),
      chainId: 100,
    };
  }
}
